import { fetchAuthSession } from '@aws-amplify/auth';

// Force CI again

/**
 * Safari 16 API Compatibility Layer
 *
 * Safari 16.0 has a bug where aws-amplify/api throws "InvalidApiName: API name is invalid"
 * even when APIs are properly configured. This service detects Safari 16.0 and provides
 * a native fetch() fallback while maintaining the same interface as ChiroUpAPI.
 *
 * Usage:
 *   Safari16API.configure({
 *     api: 'https://api.example.com',
 *     'api-noauth': 'https://api.example.com'
 *   });
 *
 *   const data = await Safari16API.get('api', '/endpoint', {});
 */

// Detect Safari 16.x or lower (all versions with Amplify bugs)
const isSafari16 = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const match = ua.match(/Version\/([\d.]+)/);
  if (!match) return false;

  // Parse major version (e.g., "16.6.1" -> 16)
  const versionParts = match[1].split('.');
  const majorVersion = parseInt(versionParts[0], 10);

  // Use fallback for Safari 16.x and below
  return majorVersion <= 16;
})();

// API endpoint registry
const apiRegistry: Record<string, string> = {};

/**
 * Configure API endpoints
 * @param apis - Object mapping API names to endpoints
 */
const configure = (apis: Record<string, string>) => {
  Object.assign(apiRegistry, apis);
};

/**
 * Get endpoint for API name
 */
const getEndpoint = (apiName: string): string => {
  const endpoint = apiRegistry[apiName];

  if (!endpoint) {
    throw new Error(`API endpoint not configured for: ${apiName}`);
  }

  return endpoint;
};

/**
 * Native fetch implementation for Safari 16
 */
const fetchFallback = async <T = any>(
  apiName: string,
  path: string,
  method: string,
  options: any = {}
): Promise<T> => {
  const endpoint = getEndpoint(apiName);
  const url = new URL(path, endpoint);

  // Add query params
  if (options.queryParams) {
    Object.entries(options.queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  // Build headers
  const headers: Record<string, string> = {};

  // Add Content-Type for requests with body
  if (options.body && !options.headers?.['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Add auth header for 'api' endpoint (not for 'api-noauth')
  if (apiName === 'api') {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (authError) {
      console.warn('[Safari16API] Could not get auth session:', authError);
    }
  }

  // Merge with any custom headers
  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (options.body) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  const response = await fetch(url.toString(), fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: response.statusText,
    }));
    // Throw in same format as ChiroUpAPI for consistency
    throw {
      response: {
        data: {
          statusCode: response.status,
          ...errorData,
        },
      },
    };
  }

  // Handle different response types
  if (options.responseType === 'blob') {
    return response as unknown as T;
  }

  const json = await response.json().catch(() => null);

  return json as T;
};

/**
 * GET request - Always uses fetch fallback
 * Detection happens in ChiroUpAPI, so if we're here, we know we need the fallback
 */
const get = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'GET', options);
};

/**
 * POST request - Always uses fetch fallback
 */
const post = async <T = any>(
  apiName: any,
  path: any,
  options: any = {},
  requestOptions: { type?: 'json' | 'blob' | 'text' } = {}
): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'POST', options);
};

/**
 * PUT request - Always uses fetch fallback
 */
const put = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'PUT', options);
};

/**
 * PATCH request - Always uses fetch fallback
 */
const patch = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'PATCH', options);
};

/**
 * DELETE request - Always uses fetch fallback
 */
const del = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'DELETE', options);
};

export const Safari16API = {
  configure,
  get,
  post,
  put,
  patch,
  del,
  isSafari16,
};
